/**
 * ChangeDetectionPanel — Shows per-agent document changes from SharePoint vs last index snapshot.
 * Supports per-document and per-agent approval, indexing with progress tracking,
 * and promotion/rejection of built indexes.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

import {
  RefreshCw, CheckCircle, AlertTriangle, FileText, Loader2,
  Bot, ChevronDown, ChevronRight, Plus, Minus, Edit3,
  XCircle, Play, ArrowUpCircle, Trash2,
  CheckSquare, Shield, MessageSquare, Clock
} from 'lucide-react';
import type {
  AgentChangeDetection, ApprovalBatch, IndexingProgress, FileChange, ApprovalStatus
} from './types';
import { API_BASE, authFetch, getAgentColor, getAgentDisplayName } from './constants';
import IndexTestPanel from './IndexTestPanel';
import ImageModePicker from './ImageModePicker';
import { captionToImageMode, getResolvedImageModeOptions, type ImageModeChoice } from './imageModeOptions';

/** Map a raw agent_configs.py image_mode string to the picker's choice type,
 * falling back to the baseline default ('caption') for anything unrecognized
 * (unknown agent, Function App unreachable, etc.) — see AA-220. */
const rawModeToChoice = (raw: string | null | undefined): ImageModeChoice =>
  (raw && ['skip', 'caption', 'marker', 'bbox_horizontal', 'bbox_vertical', 'page_image', 'auto'].includes(raw))
    ? raw as ImageModeChoice
    : 'caption';

interface ChangeDetectionPanelProps {
  selectedEnvironment: string;
  onRefreshIndexes?: () => void;
}

const STATUS_COLORS: Record<ApprovalStatus, { bg: string; text: string; border: string }> = {
  pending:   { bg: '#fef3c7', text: '#92400e', border: '#fbbf24' },
  approved:  { bg: '#d1fae5', text: '#065f46', border: '#34d399' },
  indexing:  { bg: '#dbeafe', text: '#1e40af', border: '#60a5fa' },
  testing:   { bg: '#d1fae5', text: '#065f46', border: '#34d399' },
  promoted:  { bg: '#d1fae5', text: '#065f46', border: '#10b981' },
  rejected:  { bg: '#fee2e2', text: '#991b1b', border: '#f87171' },
  cancelled: { bg: '#f1f2f4', text: '#6f747b', border: '#cfd2d6' },
  failed:    { bg: '#fee2e2', text: '#991b1b', border: '#f87171' },
};

const ChangeDetectionPanel: React.FC<ChangeDetectionPanelProps> = ({
  selectedEnvironment, onRefreshIndexes
}) => {
  const [agents, setAgents] = useState<AgentChangeDetection[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanningAgents, setScanningAgents] = useState<Set<string>>(new Set());
  const [scannedCount, setScannedCount] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [expandedAgentFiles, setExpandedAgentFiles] = useState<Record<string, AgentChangeDetection>>({});
  const [expandLoading, setExpandLoading] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Record<string, Set<string>>>({});
  const [batches, setBatches] = useState<ApprovalBatch[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [progressPolling, setProgressPolling] = useState<Record<string, NodeJS.Timeout>>({});
  const [view, setView] = useState<'changes' | 'approvals'>('changes');
  const [testingBatch, setTestingBatch] = useState<ApprovalBatch | null>(null);
  const [seeding, setSeeding] = useState<string | null>(null);
  const [seedProgress, setSeedProgress] = useState<{ total: number; done: number; current: string; results: Array<{ agentId: string; displayName: string; success: boolean; totalFiles?: number; error?: string }> } | null>(null);
  const [autoPromoteSettings, setAutoPromoteSettings] = useState<Record<string, boolean>>({});
  const [autoPromoteLoading, setAutoPromoteLoading] = useState<string | null>(null);
  const [autoIndexSettings, setAutoIndexSettings] = useState<Record<string, { enabled: boolean; intervalMs: number; debounceMs: number }>>({});
  const [autoIndexLoading, setAutoIndexLoading] = useState<string | null>(null);
  const [autoIndexStatus, setAutoIndexStatus] = useState<{ running: boolean; lastPollTime: string | null; recentHistory: any[] } | null>(null);
  // Live agent_configs.py shape, keyed by agentId (AA-220) — populates the
  // image-mode picker's pre-selected value with each agent's *actual*
  // current setting instead of an opaque "Default" placeholder.
  const [agentImageConfigs, setAgentImageConfigs] = useState<Record<string, { image_mode?: string; max_vision_calls?: number | null }>>({});
  // Actual configured vision deployment (AZURE_OPENAI_VISION_DEPLOYMENT app
  // setting on the Function App) — threaded into ImageModePicker so its copy
  // never hardcodes "GPT-4o" independent of what's really configured
  // (AA-220 follow-up).
  const [visionModel, setVisionModel] = useState<string | undefined>(undefined);
  const [imageModeChoice, setImageModeChoice] = useState<Record<string, ImageModeChoice>>({});
  // Per-document image strategy overrides (AA-220 follow-up — "apply image
  // strategy per document, not just the batch as a whole"). Keyed by
  // agentId, then by file path; a missing/'default' entry means "inherit
  // the agent-level choice above" for that one file. Only files that
  // actually diverge from the batch default are sent to the backend.
  const [fileImageModeOverrides, setFileImageModeOverridesState] = useState<Record<string, Record<string, ImageModeChoice>>>({});
  const [maxVisionOverride, setMaxVisionOverride] = useState<Record<string, string>>({});
  const progressRef = useRef(progressPolling);
  progressRef.current = progressPolling;
  const changesAbortRef = useRef<AbortController | null>(null);
  const batchesAbortRef = useRef<AbortController | null>(null);

  const fetchAutoPromoteSettings = useCallback(async () => {
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/auto-promote?environment=${selectedEnvironment}`
      );
      const data = await response.json();
      if (data.success) setAutoPromoteSettings(data.settings || {});
    } catch (_) {}
  }, [selectedEnvironment]);

  const toggleAutoPromote = async (agentId: string, currentValue: boolean) => {
    setAutoPromoteLoading(agentId);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/auto-promote/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environment: selectedEnvironment, enabled: !currentValue }),
      });
      const data = await response.json();
      if (data.success) {
        setAutoPromoteSettings(prev => ({ ...prev, [agentId]: !currentValue }));
      } else {
        alert(data.error || 'Failed to update auto-promote');
      }
    } catch (err: any) {
      alert(`Failed to update: ${err.message}`);
    } finally {
      setAutoPromoteLoading(null);
    }
  };

  const fetchAutoIndexSettings = useCallback(async () => {
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/auto-index/settings?environment=${selectedEnvironment}`
      );
      const data = await response.json();
      if (data.success) {
        const map: Record<string, { enabled: boolean; intervalMs: number; debounceMs: number }> = {};
        for (const agent of data.agents || []) {
          map[agent.agentId] = agent.autoIndex;
        }
        setAutoIndexSettings(map);
      }
    } catch (_) {}
  }, [selectedEnvironment]);

  const fetchAutoIndexStatus = useCallback(async () => {
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/auto-index/status`);
      const data = await response.json();
      if (data.success) setAutoIndexStatus(data);
    } catch (_) {}
  }, []);

  const fetchAgentImageConfigs = useCallback(async () => {
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/agent-image-configs?environment=${selectedEnvironment}`
      );
      const data = await response.json();
      if (data.success) {
        setAgentImageConfigs(data.configs || {});
        if (data.visionModel) setVisionModel(data.visionModel);
        // Pre-select each agent's picker to its *actual* live default the
        // first time we see it. Never overwrite a value the admin already
        // changed in this session.
        setImageModeChoice(prev => {
          const next = { ...prev };
          for (const [agentId, cfg] of Object.entries(data.configs || {})) {
            if (next[agentId] === undefined) {
              next[agentId] = rawModeToChoice((cfg as any)?.image_mode);
            }
          }
          return next;
        });
      }
    } catch (_) {}
  }, [selectedEnvironment]);

  const getImageModeChoice = (agentId: string): ImageModeChoice =>
    imageModeChoice[agentId] ?? rawModeToChoice(agentImageConfigs[agentId]?.image_mode);

  const setImageModeForAgent = (agentId: string, choice: ImageModeChoice) =>
    setImageModeChoice(prev => ({ ...prev, [agentId]: choice }));

  const getMaxVisionOverride = (agentId: string): string => maxVisionOverride[agentId] ?? '';

  const setMaxVisionForAgent = (agentId: string, value: string) =>
    setMaxVisionOverride(prev => ({ ...prev, [agentId]: value }));

  /** Per-file override for one document, or 'default' to inherit the
   * agent-level choice above (AA-220 follow-up). */
  const getFileImageMode = (agentId: string, filePath: string): ImageModeChoice =>
    fileImageModeOverrides[agentId]?.[filePath] ?? 'default';

  const setFileImageMode = (agentId: string, filePath: string, choice: ImageModeChoice) =>
    setFileImageModeOverridesState(prev => ({
      ...prev,
      [agentId]: { ...prev[agentId], [filePath]: choice },
    }));

  /** Build the `{filename: image_mode}` payload for the files that actually
   * diverge from this run's batch-level default — an empty/absent map means
   * "no per-document overrides", so callers can omit the field entirely.
   * Keyed by basename (matches UniversalIndexer._image_mode_override_for on
   * the Function App side, which only has each file's name to go on). */
  const buildFileImageModeOverrides = (
    agentId: string, files: FileChange[],
  ): Record<string, string> | undefined => {
    const perAgent = fileImageModeOverrides[agentId];
    if (!perAgent) return undefined;
    const overrides: Record<string, string> = {};
    for (const file of files) {
      const choice = perAgent[file.path];
      if (!choice || choice === 'default') continue;
      const mode = captionToImageMode(choice);
      if (mode) overrides[file.name || file.path] = mode;
    }
    return Object.keys(overrides).length > 0 ? overrides : undefined;
  };

  const toggleAutoIndex = async (agentId: string, currentEnabled: boolean) => {
    setAutoIndexLoading(agentId);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/auto-index/settings/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          environment: selectedEnvironment,
          enabled: !currentEnabled,
          intervalMs: autoIndexSettings[agentId]?.intervalMs || 1800000,
          debounceMs: autoIndexSettings[agentId]?.debounceMs || 60000,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setAutoIndexSettings(prev => ({
          ...prev,
          [agentId]: { ...prev[agentId], enabled: !currentEnabled },
        }));
      } else {
        alert(data.error || 'Failed to update auto-index');
      }
    } catch (err: any) {
      alert(`Failed to update: ${err.message}`);
    } finally {
      setAutoIndexLoading(null);
    }
  };

  const fetchChanges = useCallback(async () => {
    changesAbortRef.current?.abort();
    const controller = new AbortController();
    changesAbortRef.current = controller;
    setLoading(true);
    setError(null);
    setAgents([]);
    setScanningAgents(new Set());
    setScannedCount({ done: 0, total: 0 });

    try {
      // 1) Fetch agent registry instantly (no SharePoint scanning)
      const regRes = await authFetch(
        `${API_BASE}/admin/indexing/changes/registry`,
        { signal: controller.signal }
      );
      const regData = await regRes.json();
      if (controller.signal.aborted) return;
      if (!regData.success) { setError(regData.error || 'Failed to load agent registry'); setLoading(false); return; }

      const registryAgents: Array<{ agentId: string; displayName: string; lifecycle: string; note?: string; askaldokbFolder?: string }> = regData.agents || [];

      // 2) Separate non-managed (instant) from managed (need scanning)
      const nonManaged = ['external', 'custom', 'deferred'];
      const instantAgents: AgentChangeDetection[] = registryAgents
        .filter(a => nonManaged.includes(a.lifecycle))
        .map(a => ({
          agentId: a.agentId,
          displayName: a.displayName,
          askaldokbFolder: a.askaldokbFolder || '',
          changes: { added: [], modified: [], removed: [] },
          unchangedCount: 0,
          totalChanges: 0,
          hasChanges: false,
          lastIndexed: null,
          lastIndexName: null,
          lifecycle: a.lifecycle,
          note: a.note,
        }));

      const managedAgents = registryAgents.filter(a => !nonManaged.includes(a.lifecycle));

      // Show non-managed agents immediately; managed ones will appear as they complete
      setAgents(instantAgents);
      setLoading(false);

      const scanSet = new Set(managedAgents.map(a => a.agentId));
      setScanningAgents(scanSet);
      setScannedCount({ done: 0, total: managedAgents.length });

      // 3) Scan managed agents with concurrency limit (3 at a time)
      const CONCURRENCY = 3;
      let doneCount = 0;
      const queue = [...managedAgents];

      const scanNext = async (): Promise<void> => {
        while (queue.length > 0) {
          const agent = queue.shift();
          if (!agent || controller.signal.aborted) return;
          try {
            const res = await authFetch(
              `${API_BASE}/admin/indexing/changes/${agent.agentId}?environment=${selectedEnvironment}`,
              { signal: controller.signal }
            );
            const data = await res.json();
            if (controller.signal.aborted) return;
            if (data.success !== false) {
              setAgents(prev => [...prev, data as AgentChangeDetection]);
            }
          } catch (err: any) {
            if (err.name === 'AbortError') return;
            setAgents(prev => [...prev, {
              agentId: agent.agentId,
              displayName: agent.displayName,
              askaldokbFolder: agent.askaldokbFolder || '',
              changes: { added: [], modified: [], removed: [] },
              unchangedCount: 0,
              totalChanges: 0,
              hasChanges: false,
              lastIndexed: null,
              lastIndexName: null,
              error: err.message,
            } as AgentChangeDetection]);
          } finally {
            if (!controller.signal.aborted) {
              doneCount++;
              setScannedCount({ done: doneCount, total: managedAgents.length });
              setScanningAgents(prev => { const next = new Set(prev); next.delete(agent.agentId); return next; });
            }
          }
        }
      };

      await Promise.all(Array.from({ length: CONCURRENCY }, () => scanNext()));
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to connect');
      setLoading(false);
    }
  }, [selectedEnvironment]);

  const fetchBatches = useCallback(async () => {
    batchesAbortRef.current?.abort();
    const controller = new AbortController();
    batchesAbortRef.current = controller;
    setBatchLoading(true);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/approvals?environment=${selectedEnvironment}`,
        { signal: controller.signal }
      );
      const data = await response.json();
      if (controller.signal.aborted) return;
      if (data.success) {
        setBatches(data.batches || []);
        for (const batch of (data.batches || [])) {
          if (batch.status === 'indexing' && !progressRef.current[batch.batchId]) {
            startProgressPolling(batch.batchId, batch.environment, batch.agentId);
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching batches:', err);
    } finally {
      if (!controller.signal.aborted) setBatchLoading(false);
    }
  }, [selectedEnvironment]);

  useEffect(() => {
    fetchChanges();
    fetchBatches();
    fetchAutoPromoteSettings();
    fetchAutoIndexSettings();
    fetchAutoIndexStatus();
    fetchAgentImageConfigs();
    return () => {
      changesAbortRef.current?.abort();
      batchesAbortRef.current?.abort();
      Object.values(progressRef.current).forEach(clearInterval);
    };
  }, [fetchChanges, fetchBatches, fetchAutoPromoteSettings, fetchAutoIndexSettings, fetchAutoIndexStatus, fetchAgentImageConfigs]);

  const fetchAgentDetail = useCallback(async (agentId: string) => {
    if (expandedAgentFiles[agentId]) return;
    setExpandLoading(agentId);
    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/changes/${agentId}?environment=${selectedEnvironment}`
      );
      const data = await response.json();
      if (data.success) {
        setExpandedAgentFiles(prev => ({ ...prev, [agentId]: data }));
      }
    } catch (err) {
      console.error('Error fetching agent detail:', err);
    } finally {
      setExpandLoading(null);
    }
  }, [selectedEnvironment, expandedAgentFiles]);

  const handleSeedSnapshot = async (agentId: string) => {
    setSeeding(agentId);
    const agent = agents.find(a => a.agentId === agentId);
    setSeedProgress({ total: 1, done: 0, current: agent?.displayName || agentId, results: [] });
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/snapshots/${agentId}/seed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ environment: selectedEnvironment }),
      });
      const data = await response.json();
      const result = {
        agentId,
        displayName: agent?.displayName || agentId,
        success: !!data.success,
        totalFiles: data.totalFiles,
        error: data.error,
      };
      setSeedProgress(prev => prev ? { ...prev, done: 1, current: '', results: [result] } : null);
      if (data.success) {
        fetchChanges();
      }
    } catch (err: any) {
      setSeedProgress(prev => prev ? {
        ...prev, done: 1, current: '',
        results: [{ agentId, displayName: agent?.displayName || agentId, success: false, error: err.message }],
      } : null);
    } finally {
      setSeeding(null);
    }
  };

  const handleSeedAll = async () => {
    const neverTracked = agents.filter(a => (a as any).neverTracked);
    const seedable = neverTracked.filter(a => !['external', 'custom', 'deferred'].includes((a as any).lifecycle));
    if (seedable.length === 0) return;

    setSeeding('all');
    const results: Array<{ agentId: string; displayName: string; success: boolean; totalFiles?: number; error?: string }> = [];
    setSeedProgress({ total: seedable.length, done: 0, current: seedable[0].displayName, results: [] });

    for (let i = 0; i < seedable.length; i++) {
      const agent = seedable[i];
      setSeedProgress(prev => prev ? { ...prev, current: agent.displayName, done: i } : null);
      try {
        const response = await authFetch(`${API_BASE}/admin/indexing/snapshots/${agent.agentId}/seed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ environment: selectedEnvironment }),
        });
        const data = await response.json();
        results.push({
          agentId: agent.agentId,
          displayName: agent.displayName,
          success: !!data.success,
          totalFiles: data.totalFiles,
          error: data.error || (!data.success ? `HTTP ${response.status}` : undefined),
        });
      } catch (err: any) {
        results.push({
          agentId: agent.agentId,
          displayName: agent.displayName,
          success: false,
          error: err.message,
        });
      }
      setSeedProgress(prev => prev ? { ...prev, done: i + 1, results: [...results] } : null);
    }

    setSeeding(null);
    fetchChanges();
  };

  const startProgressPolling = (batchId: string, environment: string, agentId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await authFetch(
          `${API_BASE}/admin/indexing/approvals/${batchId}/progress?environment=${encodeURIComponent(environment)}&agentId=${encodeURIComponent(agentId)}`
        );
        const data = await response.json();
        if (data.success) {
          setBatches(prev => prev.map(b =>
            b.batchId === batchId ? { ...b, progress: data as IndexingProgress } : b
          ));
          if (data.phase === 'complete' || data.phase === 'failed') {
            clearInterval(progressRef.current[batchId]);
            setProgressPolling(prev => { const n = { ...prev }; delete n[batchId]; return n; });
            fetchBatches();
          }
        }
      } catch (_) {}
    }, 3000);
    setProgressPolling(prev => ({ ...prev, [batchId]: interval }));
  };

  const toggleFileSelection = (agentId: string, filePath: string) => {
    setSelectedFiles(prev => {
      const agentSet = new Set(prev[agentId] || []);
      if (agentSet.has(filePath)) agentSet.delete(filePath);
      else agentSet.add(filePath);
      return { ...prev, [agentId]: agentSet };
    });
  };

  const selectAllForAgent = (agentId: string, allFiles: FileChange[]) => {
    setSelectedFiles(prev => ({
      ...prev,
      [agentId]: new Set(allFiles.map(f => f.path)),
    }));
  };

  const deselectAllForAgent = (agentId: string) => {
    setSelectedFiles(prev => ({ ...prev, [agentId]: new Set() }));
  };

  const handleApproveAgent = async (agent: AgentChangeDetection, selectedOnly: boolean) => {
    const detail = expandedAgentFiles[agent.agentId];
    const changes = detail?.changes || agent.changes;
    const selected = selectedFiles[agent.agentId];
    const filesToApprove = selectedOnly && selected?.size
      ? {
          added: (changes.added || []).filter(f => selected.has(f.path)),
          modified: (changes.modified || []).filter(f => selected.has(f.path)),
          removed: (changes.removed || []).filter(f => selected.has(f.path)),
        }
      : changes;

    const totalSelected = (filesToApprove.added?.length || 0)
      + (filesToApprove.modified?.length || 0)
      + (filesToApprove.removed?.length || 0);
    if (totalSelected === 0) return;

    const allApprovedFiles = [
      ...(filesToApprove.added || []), ...(filesToApprove.modified || []), ...(filesToApprove.removed || []),
    ];
    const choice = getImageModeChoice(agent.agentId);
    const cap = getMaxVisionOverride(agent.agentId);

    try {
      // Create approval batch. The image strategy (run-wide + any
      // per-document overrides) is captured now so it survives to whenever
      // indexing actually starts, which may be a separate later action in
      // the Approval Pipeline tab (AA-220 follow-up).
      const createRes = await authFetch(`${API_BASE}/admin/indexing/approvals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agentId: agent.agentId,
          environment: selectedEnvironment,
          changes: filesToApprove,
          scope: selectedOnly ? 'partial' : 'full',
          imageMode: captionToImageMode(choice) || '',
          maxVisionCalls: cap === '' ? '' : Number(cap),
          imageModeOverrides: buildFileImageModeOverrides(agent.agentId, allApprovedFiles),
        }),
      });
      const createData = await createRes.json();
      if (!createData.success) throw new Error(createData.error);

      // Auto-approve it
      const approveRes = await authFetch(`${API_BASE}/admin/indexing/approvals/${createData.batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'approve',
          agentId: agent.agentId,
          environment: selectedEnvironment,
        }),
      });
      const approveData = await approveRes.json();
      if (!approveData.success) throw new Error(approveData.error);

      deselectAllForAgent(agent.agentId);
      fetchBatches();
      setView('approvals');
    } catch (err: any) {
      alert(`Approval failed: ${err.message}`);
    }
  };

  const handleForceReindex = async (agentId: string) => {
    const choice = getImageModeChoice(agentId);
    const cap = getMaxVisionOverride(agentId);
    if (!window.confirm(`Force re-index ALL files for ${getAgentDisplayName(agentId)}? This creates a new index version from scratch.`)) return;
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/force-reindex/${agentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environment: selectedEnvironment,
          imageMode: captionToImageMode(choice) || '',
          maxVisionCalls: cap === '' ? '' : Number(cap),
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      alert(data.message || `Indexing started for ${data.fileCount} files → ${data.indexName || data.batchId}`);
      fetchBatches();
      setView('approvals');
      if (data.batchId && data.status === 'indexing') {
        startProgressPolling(data.batchId, data.environment || selectedEnvironment, agentId);
      }
    } catch (err: any) {
      alert(`Force re-index failed: ${err.message}`);
    }
  };

  const handleApproveAndStartBatch = async (batch: ApprovalBatch) => {
    try {
      const approveRes = await authFetch(`${API_BASE}/admin/indexing/approvals/${batch.batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'approve',
          agentId: batch.agentId,
          environment: batch.environment,
        }),
      });
      const approveData = await approveRes.json();
      if (!approveData.success) throw new Error(approveData.error);
      await handleTriggerIndex({ ...batch, status: 'approved' });
    } catch (err: any) {
      alert(`Approve & start failed: ${err.message}`);
    }
  };

  const handleTriggerIndex = async (batch: ApprovalBatch) => {
    const choice = getImageModeChoice(batch.agentId);
    const cap = getMaxVisionOverride(batch.agentId);
    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/approvals/${batch.batchId}/index`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agentId: batch.agentId,
          environment: batch.environment,
          imageMode: captionToImageMode(choice) || '',
          maxVisionCalls: cap === '' ? '' : Number(cap),
        }),
      });
      const data = await response.json();
      if (data.success) {
        fetchBatches();
        startProgressPolling(batch.batchId, batch.environment, batch.agentId);
      } else {
        alert(`Index trigger failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Index trigger failed: ${err.message}`);
    }
  };

  const handlePromote = async (batch: ApprovalBatch) => {
    if (!window.confirm(
      `Promote index "${batch.indexName}" as the active index for ${batch.agentDisplayName} in ${batch.environment}?\n\nThis swaps the alias. The previous index is kept as a rollback target; the older (n-2) index is cleaned up.`
    )) return;

    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/approvals/${batch.batchId}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agentId: batch.agentId,
          environment: batch.environment,
          deleteOld: true,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`Successfully promoted ${data.indexName}.\nAlias: ${data.aliasName}\nPrevious (kept for rollback): ${data.previousIndex || 'none'}`);
        fetchBatches();
        fetchChanges();
        onRefreshIndexes?.();
      } else {
        alert(`Promotion failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Promotion failed: ${err.message}`);
    }
  };

  const handleReject = async (batch: ApprovalBatch) => {
    if (!window.confirm(
      `Reject and delete index "${batch.indexName}" for ${batch.agentDisplayName}?\n\nThis cannot be undone.`
    )) return;

    try {
      const response = await authFetch(`${API_BASE}/admin/indexing/approvals/${batch.batchId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agentId: batch.agentId, environment: batch.environment }),
      });
      const data = await response.json();
      if (data.success) {
        fetchBatches();
      } else {
        alert(`Rejection failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Rejection failed: ${err.message}`);
    }
  };

  const stopProgressPolling = (batchId: string) => {
    const interval = progressRef.current[batchId];
    if (interval) {
      clearInterval(interval);
      setProgressPolling(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    }
  };

  const handleCancelBatch = async (batch: ApprovalBatch) => {
    const labels: Record<string, string> = {
      indexing: `Cancel indexing for ${batch.agentDisplayName}?\n\nBatch: ${batch.indexName || batch.batchId}\n\nThe Function App job may still run in Azure, but this batch will be marked cancelled and removed from the active list.`,
      failed: `Dismiss this failed batch for ${batch.agentDisplayName}?`,
      approved: `Cancel this approved batch for ${batch.agentDisplayName}? It has not been indexed yet.`,
      pending: `Cancel this pending approval for ${batch.agentDisplayName}?`,
    };
    const msg = labels[batch.status] || `Cancel this batch for ${batch.agentDisplayName}?`;
    if (!window.confirm(msg)) return;

    try {
      const response = await authFetch(
        `${API_BASE}/admin/indexing/approvals/${batch.batchId}?agentId=${encodeURIComponent(batch.agentId)}&environment=${encodeURIComponent(batch.environment)}`,
        { method: 'DELETE' }
      );
      const data = await response.json();
      if (!data.success) {
        alert(`Cancel failed: ${data.error || 'Unknown error'}`);
        return;
      }
      stopProgressPolling(batch.batchId);
      fetchBatches();
    } catch (err: any) {
      alert(`Cancel failed: ${err.message}`);
    }
  };

  const nonManaged = ['external', 'custom', 'deferred'];
  const agentsWithChanges = agents.filter(a => a.hasChanges && !(a as any).neverTracked && !nonManaged.includes((a as any).lifecycle));
  const agentsNeverTracked = agents.filter(a => (a as any).neverTracked && !nonManaged.includes((a as any).lifecycle));
  const agentsExternal = agents.filter(a => (a as any).lifecycle === 'external');
  const agentsCustom = agents.filter(a => (a as any).lifecycle === 'custom');
  const agentsDeferred = agents.filter(a => (a as any).lifecycle === 'deferred');
  const agentsWithoutChanges = agents.filter(a => !a.hasChanges && !a.error && !(a as any).neverTracked && !nonManaged.includes((a as any).lifecycle));
  const agentsWithErrors = agents.filter(a => !!a.error && !nonManaged.includes((a as any).lifecycle));
  const activeBatches = batches.filter(b => !['promoted', 'rejected', 'cancelled'].includes(b.status));

  const formatDate = (d: string | null) => {
    if (!d) return 'Never';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const renderChangeIcon = (type: string) => {
    switch (type) {
      case 'added': return <Plus size={14} style={{ color: '#16a34a' }} />;
      case 'modified': return <Edit3 size={14} style={{ color: '#059669' }} />;
      case 'removed': return <Minus size={14} style={{ color: '#dc2626' }} />;
      default: return null;
    }
  };

  return (
    <div>
      {/* Header with view toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setView('changes')}
            style={{
              padding: '4px 12px', borderRadius: 16, border: '1px solid var(--idx-border)', cursor: 'pointer',
              background: view === 'changes' ? '#2b2e32' : 'var(--idx-bg-primary)',
              color: view === 'changes' ? 'var(--idx-bg-primary)' : 'var(--idx-text-secondary)',
              fontSize: 12, fontWeight: 500,
            }}
          >
            Pending Changes {agentsWithChanges.length > 0 && `(${agentsWithChanges.length})`}
          </button>
          <button
            onClick={() => { setView('approvals'); fetchBatches(); }}
            style={{
              padding: '4px 12px', borderRadius: 16, border: '1px solid var(--idx-border)', cursor: 'pointer',
              background: view === 'approvals' ? '#2b2e32' : 'var(--idx-bg-primary)',
              color: view === 'approvals' ? 'var(--idx-bg-primary)' : 'var(--idx-text-secondary)',
              fontSize: 12, fontWeight: 500,
            }}
          >
            Approval Pipeline {activeBatches.length > 0 && `(${activeBatches.length})`}
          </button>
        </div>
        <button
          onClick={() => { fetchChanges(); fetchBatches(); }}
          disabled={loading}
          style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer',
            background: 'var(--idx-bg-primary)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
          }}
        >
          <RefreshCw size={14} className={loading ? 'spinning' : ''} /> Refresh
        </button>
      </div>

      {/* Prod locked banner */}
      {selectedEnvironment === 'prod' && (
        <div style={{
          padding: '12px 16px', background: '#fef9c3', border: '1px solid #fde047',
          borderRadius: 8, marginBottom: 16, fontSize: 13,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Shield size={16} style={{ color: '#a16207' }} />
            <span style={{ fontWeight: 600, color: '#854d0e' }}>Production Environment</span>
          </div>
          <p style={{ color: '#713f12', margin: 0, fontSize: 12 }}>
            Production indexing uses prod Azure credentials on the shared function app hub.
            If index operations fail, verify PROD_* app settings on func-sharepoint-webhook-lab
            (Search: srch-askaldo-searchsvc-prod-centralus) and network access to prod OpenAI.
          </p>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
          <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
          {error}
        </div>
      )}

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 40, color: 'var(--idx-text-muted)', gap: 8 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ fontWeight: 500 }}>Loading agent registry...</span>
        </div>
      )}

      {/* Scanning progress bar — visible while per-agent scans are running */}
      {scanningAgents.size > 0 && !loading && (
        <div style={{
          padding: '10px 16px', background: 'var(--idx-bg-inset)', border: '1px solid #bfdbfe',
          borderRadius: 8, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
            <span style={{ color: '#059669', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              Scanning SharePoint folders... ({scannedCount.done}/{scannedCount.total})
            </span>
            <span style={{ color: 'var(--idx-text-muted)' }}>
              {Array.from(scanningAgents).slice(0, 3).map(id => getAgentDisplayName(id)).join(', ')}
              {scanningAgents.size > 3 && ` +${scanningAgents.size - 3} more`}
            </span>
          </div>
          <div style={{ height: 4, background: '#dbeafe', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: '#10b981',
              width: scannedCount.total > 0 ? `${Math.round((scannedCount.done / scannedCount.total) * 100)}%` : '0%',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* ================ CHANGES VIEW ================ */}
      {view === 'changes' && !loading && (
        <>
          {/* Never-tracked agents — seed banner (shown first, most important) */}
          {agentsNeverTracked.length > 0 && (
            <div style={{
              padding: '16px 20px', background: '#eff6ff', border: '1px solid #bfdbfe',
              borderRadius: 8, marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={16} style={{ color: '#059669' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#1e40af' }}>No Baseline Snapshot</span>
              </div>
              <p style={{ color: '#1e40af', fontSize: 13, margin: '0 0 8px' }}>
                {agentsNeverTracked.length} agent{agentsNeverTracked.length !== 1 ? 's have' : ' has'} never been tracked.
                All files appear as "added" because no baseline exists. Seed the current SharePoint state as the baseline
                to start tracking only new changes.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={handleSeedAll}
                  disabled={!!seeding}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: 'none',
                    cursor: seeding ? 'not-allowed' : 'pointer',
                    background: '#059669', color: '#fff', fontSize: 12, fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {seeding === 'all' ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                  Seed All ({agentsNeverTracked.length})
                </button>
                {agentsNeverTracked.map(a => (
                  <button
                    key={a.agentId}
                    onClick={() => handleSeedSnapshot(a.agentId)}
                    disabled={!!seeding}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: '1px solid #93c5fd',
                      cursor: seeding ? 'not-allowed' : 'pointer',
                      background: seeding === a.agentId ? '#dbeafe' : 'var(--idx-bg-primary)',
                      color: '#1e40af', fontSize: 11, fontWeight: 500,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    {seeding === a.agentId && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                    <Bot size={12} style={{ color: getAgentColor(a.agentId) }} />
                    {a.displayName} ({(a as any).changeCounts ? ((a as any).changeCounts.added + (a as any).changeCounts.modified + (a as any).changeCounts.removed) : a.totalChanges})
                  </button>
                ))}
              </div>

              {/* Seed progress indicator */}
              {seedProgress && seeding && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--idx-bg-primary)', border: '1px solid #93c5fd', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: '#059669', fontWeight: 500 }}>
                      <Loader2 size={13} style={{ display: 'inline', animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} />
                      Seeding {seedProgress.current}...
                    </span>
                    <span style={{ color: 'var(--idx-text-muted)' }}>{seedProgress.done}/{seedProgress.total}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--idx-border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                      width: `${Math.round((seedProgress.done / seedProgress.total) * 100)}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              )}

              {/* Seed results summary */}
              {seedProgress && !seeding && seedProgress.results.length > 0 && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border-strong)', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--idx-text-secondary)' }}>
                      Seed Complete — {seedProgress.results.filter(r => r.success).length}/{seedProgress.results.length} succeeded
                    </span>
                    <button
                      onClick={() => setSeedProgress(null)}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--idx-text-faint)', fontSize: 18, lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </div>
                  {seedProgress.results.map(r => (
                    <div key={r.agentId} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
                      fontSize: 12, color: r.success ? '#065f46' : '#991b1b',
                    }}>
                      {r.success
                        ? <CheckCircle size={14} style={{ color: '#10b981', flexShrink: 0 }} />
                        : <XCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
                      }
                      <span style={{ fontWeight: 500 }}>{r.displayName}</span>
                      {r.success && <span style={{ color: 'var(--idx-text-muted)' }}>({r.totalFiles} files)</span>}
                      {r.error && <span style={{ color: '#dc2626', fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* External agents */}
          {agentsExternal.length > 0 && (
            <div style={{
              padding: '12px 16px', background: 'var(--idx-bg-success)', border: '1px solid #6ee7b7',
              borderRadius: 8, marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, color: '#065f46', fontWeight: 500, marginBottom: 4 }}>
                <Shield size={14} style={{ display: 'inline', marginRight: 6 }} />
                Externally Managed
              </div>
              {agentsExternal.map(a => (
                <div key={a.agentId} style={{ fontSize: 12, color: '#047857', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Bot size={14} style={{ color: getAgentColor(a.agentId) }} />
                  <span style={{ fontWeight: 500 }}>{a.displayName}</span>
                  <span style={{ color: '#059669', fontSize: 11 }}>{(a as any).note || 'Index managed outside lifecycle system'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Custom pipeline agents */}
          {agentsCustom.length > 0 && (
            <div style={{
              padding: '12px 16px', background: '#eff6ff', border: '1px solid #93c5fd',
              borderRadius: 8, marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 500, marginBottom: 4 }}>
                <Shield size={14} style={{ display: 'inline', marginRight: 6 }} />
                Custom Pipeline
              </div>
              {agentsCustom.map(a => (
                <div key={a.agentId} style={{ fontSize: 12, color: '#1d4ed8', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Bot size={14} style={{ color: getAgentColor(a.agentId) }} />
                  <span style={{ fontWeight: 500 }}>{a.displayName}</span>
                  <span style={{ color: '#10b981', fontSize: 11 }}>{(a as any).note || 'Uses a dedicated indexing pipeline'}</span>
                </div>
              ))}
            </div>
          )}

          {/* Deferred agents */}
          {agentsDeferred.length > 0 && (
            <div style={{
              padding: '12px 16px', background: '#fefce8', border: '1px solid #fde68a',
              borderRadius: 8, marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, color: '#92400e', fontWeight: 500, marginBottom: 4 }}>
                <Clock size={14} style={{ display: 'inline', marginRight: 6 }} />
                Planned Migration
              </div>
              {agentsDeferred.map(a => (
                <div key={a.agentId} style={{ fontSize: 12, color: '#b45309', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Bot size={14} style={{ color: getAgentColor(a.agentId) }} />
                  <span style={{ fontWeight: 500 }}>{a.displayName}</span>
                  <span style={{ color: '#d97706', fontSize: 11 }}>{(a as any).note || 'Will be migrated to universal indexer'}</span>
                </div>
              ))}
            </div>
          )}

          {agentsWithChanges.length === 0 && agentsWithErrors.length === 0 && agentsNeverTracked.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--idx-text-muted)' }}>
              <CheckCircle size={32} style={{ margin: '0 auto 12px', color: '#10b981' }} />
              <p style={{ fontWeight: 500, margin: '0 0 4px' }}>All indexes are up to date</p>
              <p style={{ fontSize: 13 }}>No document changes detected since the last index run.</p>
            </div>
          )}

          {agentsWithChanges.map(agent => {
            const isExpanded = expandedAgent === agent.agentId;
            const selected = selectedFiles[agent.agentId] || new Set();
            const detail = expandedAgentFiles[agent.agentId];
            const fullChanges = detail?.changes || agent.changes;
            const allFiles = [...(fullChanges.added || []), ...(fullChanges.modified || []), ...(fullChanges.removed || [])];
            const color = getAgentColor(agent.agentId);

            return (
              <div key={agent.agentId} style={{
                border: '1px solid var(--idx-border)', borderRadius: 8, marginBottom: 12,
                borderLeft: `4px solid ${color}`, overflow: 'hidden',
              }}>
                {/* Agent header */}
                <div
                  onClick={() => {
                    const next = isExpanded ? null : agent.agentId;
                    setExpandedAgent(next);
                    if (next) fetchAgentDetail(next);
                  }}
                  style={{
                    padding: '12px 16px', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', cursor: 'pointer', background: 'var(--idx-bg-secondary)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Bot size={18} style={{ color }} />
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{agent.displayName}</span>
                      <span style={{ color: 'var(--idx-text-muted)', fontSize: 12, marginLeft: 8 }}>
                        {agent.askaldokbFolder}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {((agent as any).changeCounts?.added || agent.changes.added.length) > 0 && (
                      <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 500 }}>
                        +{(agent as any).changeCounts?.added || agent.changes.added.length} added
                      </span>
                    )}
                    {((agent as any).changeCounts?.modified || agent.changes.modified.length) > 0 && (
                      <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>
                        ~{(agent as any).changeCounts?.modified || agent.changes.modified.length} modified
                      </span>
                    )}
                    {((agent as any).changeCounts?.removed || agent.changes.removed.length) > 0 && (
                      <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 500 }}>
                        -{(agent as any).changeCounts?.removed || agent.changes.removed.length} removed
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>
                      Last indexed: {formatDate(agent.lastIndexed)}
                    </span>
                  </div>
                </div>

                {/* Expanded file list */}
                {isExpanded && expandLoading === agent.agentId && (
                  <div style={{ padding: 20, textAlign: 'center', borderTop: '1px solid var(--idx-border)', color: 'var(--idx-text-muted)' }}>
                    <Loader2 size={18} style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />
                    <span style={{ marginLeft: 8, fontSize: 13 }}>Loading file details...</span>
                  </div>
                )}
                {isExpanded && expandLoading !== agent.agentId && (
                  <div style={{ borderTop: '1px solid var(--idx-border)' }}>
                    {/* Image strategy — pre-selected to this agent's live agent_configs.py
                        default (AA-220); editable before Force Re-index runs. */}
                    <div style={{
                      padding: '10px 16px', background: 'var(--idx-bg-tertiary)',
                      borderBottom: '1px solid var(--idx-bg-tertiary)',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                        Image Strategy for this run
                      </div>
                      <ImageModePicker
                        value={getImageModeChoice(agent.agentId)}
                        onChange={choice => setImageModeForAgent(agent.agentId, choice)}
                        maxVisionCalls={getMaxVisionOverride(agent.agentId)}
                        onMaxVisionCallsChange={value => setMaxVisionForAgent(agent.agentId, value)}
                        visionModel={visionModel}
                      />
                    </div>
                    {/* Selection toolbar */}
                    <div style={{
                      padding: '8px 16px', background: 'var(--idx-bg-secondary)', display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--idx-bg-tertiary)',
                    }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          onClick={() => selectAllForAgent(agent.agentId, allFiles)}
                          style={{
                            padding: '3px 8px', borderRadius: 4, border: '1px solid var(--idx-border-strong)',
                            cursor: 'pointer', background: 'var(--idx-bg-primary)', fontSize: 11,
                          }}
                        >
                          <CheckSquare size={12} style={{ display: 'inline', marginRight: 4 }} />
                          Select All ({allFiles.length})
                        </button>
                        {selected.size > 0 && (
                          <button
                            onClick={() => deselectAllForAgent(agent.agentId)}
                            style={{
                              padding: '3px 8px', borderRadius: 4, border: '1px solid var(--idx-border-strong)',
                              cursor: 'pointer', background: 'var(--idx-bg-primary)', fontSize: 11,
                            }}
                          >
                            Clear ({selected.size})
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {selected.size > 0 && (
                          <button
                            onClick={() => handleApproveAgent(agent, true)}
                            style={{
                              padding: '4px 12px', borderRadius: 6, border: 'none',
                              cursor: 'pointer', background: '#059669', color: '#fff',
                              fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            <ArrowUpCircle size={14} /> Approve Selected ({selected.size})
                          </button>
                        )}
                        <button
                          onClick={() => handleApproveAgent(agent, false)}
                          style={{
                            padding: '4px 12px', borderRadius: 6, border: 'none',
                            cursor: 'pointer', background: '#16a34a', color: '#fff',
                            fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          <CheckCircle size={14} /> Approve All
                        </button>
                        <button
                          onClick={() => handleForceReindex(agent.agentId)}
                          style={{
                            padding: '4px 12px', borderRadius: 6, border: '1px solid var(--idx-border-strong, #cfd2d6)',
                            cursor: 'pointer', background: 'var(--idx-bg-primary, #fff)', color: 'var(--idx-text-secondary, #3d4045)',
                            fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                          }}
                          title="Re-index ALL files from scratch, ignoring change detection"
                        >
                          <RefreshCw size={14} /> Force Re-index
                        </button>
                      </div>
                    </div>

                    {/* File rows */}
                    {allFiles.map(file => {
                      const fileOverride = getFileImageMode(agent.agentId, file.path);
                      const hasOverride = fileOverride !== 'default';
                      return (
                      <div
                        key={file.path}
                        onClick={() => toggleFileSelection(agent.agentId, file.path)}
                        style={{
                          padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10,
                          borderBottom: '1px solid var(--idx-bg-tertiary)', cursor: 'pointer',
                          background: selected.has(file.path) ? '#eff6ff' : 'var(--idx-bg-primary)',
                          fontSize: 13,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(file.path)}
                          onChange={() => {}}
                          style={{ cursor: 'pointer' }}
                        />
                        {renderChangeIcon(file.changeType)}
                        <FileText size={14} style={{ color: 'var(--idx-text-muted)' }} />
                        <span style={{ flex: 1 }}>{file.path}</span>
                        {hasOverride && (
                          <span
                            title={`This file will use ${getResolvedImageModeOptions(visionModel).find(o => o.value === fileOverride)?.label || fileOverride} instead of the batch's image strategy`}
                            style={{
                              fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 9999,
                              color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            image override
                          </span>
                        )}
                        {file.lastModifiedBy && (
                          <span style={{ color: '#6366f1', fontSize: 11, fontWeight: 500 }}>
                            {file.lastModifiedBy}
                          </span>
                        )}
                        {file.size && (
                          <span style={{ color: 'var(--idx-text-faint)', fontSize: 11 }}>
                            {file.size < 1024 * 1024
                              ? `${(file.size / 1024).toFixed(1)} KB`
                              : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                          </span>
                        )}
                        {file.lastModified && (
                          <span style={{ color: 'var(--idx-text-faint)', fontSize: 11 }}>
                            {new Date(file.lastModified).toLocaleDateString()}
                          </span>
                        )}
                        {/* Per-document image strategy override (AA-220 follow-up) — 'default'
                            inherits the batch-level choice set above the file list. Stops
                            propagation so picking a mode doesn't also toggle file selection. */}
                        <select
                          value={fileOverride}
                          onClick={e => e.stopPropagation()}
                          onChange={e => setFileImageMode(agent.agentId, file.path, e.target.value as ImageModeChoice)}
                          title="Override the image strategy for this one file"
                          style={{
                            fontSize: 11, padding: '3px 6px', borderRadius: 5,
                            border: `1px solid ${hasOverride ? '#fbbf24' : 'var(--idx-border)'}`,
                            background: hasOverride ? '#fffbeb' : 'var(--idx-bg-primary)',
                            color: 'var(--idx-text-secondary)', cursor: 'pointer', maxWidth: 170,
                          }}
                        >
                          <option value="default">Same as batch above</option>
                          {getResolvedImageModeOptions(visionModel)
                            .filter(o => o.value !== 'default')
                            .map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Agents with errors */}
          {agentsWithErrors.map(agent => (
            <div key={agent.agentId} style={{
              border: '1px solid #fecaca', borderRadius: 8, marginBottom: 8,
              padding: '10px 16px', background: '#fef2f2', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <AlertTriangle size={16} style={{ color: '#dc2626' }} />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{agent.displayName}</span>
              <span style={{ color: '#991b1b', fontSize: 12, flex: 1 }}>{agent.error}</span>
            </div>
          ))}

          {/* Agents with no changes */}
          {agentsWithoutChanges.length > 0 && (
            <div style={{ marginTop: 16, padding: '10px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: 12, color: '#15803d', fontWeight: 500, marginBottom: 4 }}>
                <CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
                {agentsWithoutChanges.length} agent{agentsWithoutChanges.length !== 1 ? 's' : ''} up to date
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {agentsWithoutChanges.map(a => (
                  <div key={a.agentId} style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: '4px 8px', borderRadius: 6, background: 'var(--idx-bg-primary)',
                    border: '1px solid var(--idx-border, #e3e5e8)',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--idx-text-secondary, #3d4045)', minWidth: 130 }}>
                      <Bot size={12} style={{ display: 'inline', marginRight: 4, color: getAgentColor(a.agentId) }} />
                      {a.displayName}
                    </span>
                    <ImageModePicker
                      value={getImageModeChoice(a.agentId)}
                      onChange={choice => setImageModeForAgent(a.agentId, choice)}
                      maxVisionCalls={getMaxVisionOverride(a.agentId)}
                      onMaxVisionCallsChange={value => setMaxVisionForAgent(a.agentId, value)}
                      visionModel={visionModel}
                      compact
                      minimal
                    />
                    <button
                      onClick={() => handleForceReindex(a.agentId)}
                      style={{
                        padding: '3px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong, #cfd2d6)',
                        cursor: 'pointer', background: 'var(--idx-bg-primary, #fff)', color: 'var(--idx-text-secondary, #3d4045)',
                        fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <RefreshCw size={12} /> Force Re-index
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ================ APPROVALS VIEW ================ */}
      {view === 'approvals' && (
        <>
          {/* Auto-promote settings */}
          <div style={{
            padding: '12px 16px', background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)',
            borderRadius: 8, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ArrowUpCircle size={14} style={{ color: 'var(--idx-text-muted)' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--idx-text-secondary)' }}>Auto-Promote</span>
              </div>
              <span style={{ fontSize: 11, color: 'var(--idx-text-faint)' }}>
                When enabled, new indexes are promoted immediately after a successful build (no manual test step)
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {agents.map(agent => {
                const enabled = autoPromoteSettings[agent.agentId] || false;
                const name = agent.displayName || getAgentDisplayName(agent.agentId);
                return (
                  <button
                    key={agent.agentId}
                    onClick={() => toggleAutoPromote(agent.agentId, enabled)}
                    disabled={autoPromoteLoading === agent.agentId}
                    title={`${enabled ? 'Disable' : 'Enable'} auto-promote for ${name}`}
                    style={{
                      padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 500,
                      border: `1px solid ${enabled ? '#86efac' : '#e3e5e8'}`,
                      background: enabled ? '#f0fdf4' : 'var(--idx-bg-primary)',
                      color: enabled ? '#15803d' : '#6f747b',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Bot size={12} style={{ color: getAgentColor(agent.agentId) }} />
                    {name}
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                      background: enabled ? '#22c55e' : 'var(--idx-border-strong)',
                    }} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto-index settings */}
          <div style={{
            padding: '12px 16px', background: '#f8f9fa', border: '1px solid #e3e5e8',
            borderRadius: 8, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} style={{ color: '#6f747b' }} />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#3d4045' }}>Auto-Index</span>
                {autoIndexStatus?.running && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 8,
                    background: '#dcfce7', color: '#15803d', fontWeight: 600,
                  }}>RUNNING</span>
                )}
              </div>
              <span style={{ fontSize: 11, color: '#9da2a8' }}>
                Polls SharePoint every 30 min during business hours (8am–6pm ET) and auto re-indexes on change
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {agents.map(agent => {
                if (agent.lifecycle === 'external' || agent.lifecycle === 'deferred') return null;
                const setting = autoIndexSettings[agent.agentId] || { enabled: false };
                const name = agent.displayName || getAgentDisplayName(agent.agentId);
                return (
                  <button
                    key={agent.agentId}
                    onClick={() => toggleAutoIndex(agent.agentId, setting.enabled)}
                    disabled={autoIndexLoading === agent.agentId}
                    title={`${setting.enabled ? 'Disable' : 'Enable'} auto-index for ${name}`}
                    style={{
                      padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 500,
                      border: `1px solid ${setting.enabled ? '#93c5fd' : '#e3e5e8'}`,
                      background: setting.enabled ? '#eff6ff' : '#fff',
                      color: setting.enabled ? '#1d4ed8' : '#6f747b',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Bot size={12} style={{ color: getAgentColor(agent.agentId) }} />
                    {name}
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                      background: setting.enabled ? '#3b82f6' : '#cfd2d6',
                    }} />
                  </button>
                );
              })}
            </div>
            {autoIndexStatus?.lastPollTime && (
              <div style={{ fontSize: 11, color: '#9da2a8', marginTop: 6 }}>
                Last poll: {new Date(autoIndexStatus.lastPollTime).toLocaleString()}
              </div>
            )}
            {autoIndexStatus?.recentHistory && autoIndexStatus.recentHistory.length > 0 && (
              <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto', fontSize: 11, color: '#53575d' }}>
                {autoIndexStatus.recentHistory.slice(0, 10).map((entry: any, i: number) => (
                  <div key={i} style={{
                    padding: '2px 0', borderBottom: '1px solid #f1f2f4',
                    display: 'flex', gap: 8, alignItems: 'center',
                  }}>
                    <span style={{ color: '#9da2a8', minWidth: 130 }}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                    <span style={{
                      padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: entry.type.includes('fail') ? '#fef2f2' : entry.type.includes('promot') ? '#f0fdf4' : '#eff6ff',
                      color: entry.type.includes('fail') ? '#dc2626' : entry.type.includes('promot') ? '#15803d' : '#2563eb',
                    }}>
                      {entry.type.replace(/_/g, ' ')}
                    </span>
                    <span>{entry.agentId}</span>
                    {entry.changes && <span style={{ color: '#9da2a8' }}>({entry.changes} changes)</span>}
                    {entry.error && <span style={{ color: '#dc2626' }}>{entry.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {batchLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: 'var(--idx-text-muted)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {!batchLoading && batches.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--idx-text-muted)' }}>
              <Shield size={32} style={{ margin: '0 auto 12px', color: 'var(--idx-border-strong)' }} />
              <p style={{ fontWeight: 500, margin: '0 0 4px' }}>No approval batches</p>
              <p style={{ fontSize: 13 }}>Approve changes from the Pending Changes view to start.</p>
            </div>
          )}

          {batches.map(batch => {
            const statusStyle = STATUS_COLORS[batch.status] || STATUS_COLORS.pending;
            const changes = batch.changes || { added: [], modified: [], removed: [] };
            const totalChanges = (changes.added?.length || 0)
              + (changes.modified?.length || 0)
              + (changes.removed?.length || 0);

            return (
              <div key={batch.batchId} style={{
                border: `1px solid ${statusStyle.border}`, borderRadius: 8, marginBottom: 12,
                overflow: 'hidden',
              }}>
                {/* Batch header */}
                <div style={{
                  padding: '12px 16px', background: statusStyle.bg,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Bot size={18} style={{ color: getAgentColor(batch.agentId) }} />
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14, color: statusStyle.text }}>
                        {batch.agentDisplayName}
                      </span>
                      <span style={{
                        marginLeft: 8, padding: '2px 8px', borderRadius: 12, fontSize: 11,
                        fontWeight: 600, background: statusStyle.bg, color: statusStyle.text,
                        border: `1px solid ${statusStyle.border}`,
                      }}>
                        {batch.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--idx-text-muted)' }}>
                    <span>{totalChanges} file{totalChanges !== 1 ? 's' : ''}</span>
                    <span>{batch.scope === 'partial' ? '(partial)' : '(full)'}</span>
                    <span>{formatDate(batch.createdAt)}</span>
                  </div>
                </div>

                {/* Progress bar for indexing */}
                {batch.status === 'indexing' && batch.progress && (() => {
                  const isQueued = batch.progress.phase === 'queued';
                  const pct = batch.progress.percentComplete > 0
                    ? batch.progress.percentComplete
                    : (batch.progress.totalFiles > 0
                        ? Math.round((batch.progress.filesProcessed / batch.progress.totalFiles) * 100)
                        : 0);
                  return (
                  <div style={{ padding: '8px 16px', borderTop: `1px solid ${statusStyle.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: '#059669', fontWeight: 500 }}>
                        {isQueued ? 'Queued...' :
                         batch.progress.phase === 'discovering' ? 'Discovering SharePoint files...' :
                         batch.progress.phase === 'extracting' ? 'Extracting content...' :
                         batch.progress.phase === 'chunking' ? 'Chunking documents...' :
                         batch.progress.phase === 'embedding' ? 'Generating embeddings...' :
                         batch.progress.phase === 'uploading' ? 'Uploading to index...' :
                         batch.progress.phase === 'finalizing' ? 'Finalizing...' :
                         batch.progress.phase}
                      </span>
                      <span style={{ color: 'var(--idx-text-muted)' }}>
                        {isQueued ? 'Waiting...' : `${pct}%`}
                        {batch.progress.totalFiles > 0 && !isQueued &&
                          ` (${batch.progress.filesProcessed}/${batch.progress.totalFiles})`}
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--idx-border)', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                      {isQueued ? (
                        <div style={{
                          height: '100%', borderRadius: 3, width: '30%',
                          background: 'linear-gradient(90deg, #10b981, #059669)',
                          animation: 'indeterminateProgress 1.5s ease-in-out infinite',
                        }} />
                      ) : (
                        <div style={{
                          height: '100%', borderRadius: 3,
                          background: 'linear-gradient(90deg, #10b981, #059669)',
                          width: `${pct}%`,
                          transition: 'width 0.3s ease',
                        }} />
                      )}
                    </div>
                    {batch.progress.currentFile && (
                      <div style={{ fontSize: 11, color: 'var(--idx-text-faint)', marginTop: 4 }}>
                        {batch.progress.currentFile}
                      </div>
                    )}
                  </div>
                  );
                })()}

                {(batch.status === 'failed' || batch.progress?.error) && (
                  <div style={{
                    padding: '8px 16px', fontSize: 12, color: '#991b1b',
                    borderTop: `1px solid ${statusStyle.border}`, background: '#fef2f2',
                  }}>
                    <strong>Indexing failed:</strong>{' '}
                    {batch.progress?.error || 'See Function App logs for details.'}
                  </div>
                )}

                {/* Index name */}
                {batch.indexName && (
                  <div style={{
                    padding: '6px 16px', fontSize: 12, color: 'var(--idx-text-muted)',
                    borderTop: `1px solid ${statusStyle.border}`, background: 'var(--idx-bg-secondary)',
                  }}>
                    Index: <code style={{ background: 'var(--idx-bg-tertiary)', padding: '1px 4px', borderRadius: 3 }}>{batch.indexName}</code>
                  </div>
                )}

                {/* Image strategy — pre-selected to this agent's live agent_configs.py
                    default (AA-220); editable before the build actually starts. */}
                {(batch.status === 'pending' || batch.status === 'approved') && (
                  <div style={{
                    padding: '10px 16px', borderTop: `1px solid ${statusStyle.border}`,
                    background: 'var(--idx-bg-tertiary)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Image Strategy for this run
                    </div>
                    <ImageModePicker
                      value={getImageModeChoice(batch.agentId)}
                      onChange={choice => setImageModeForAgent(batch.agentId, choice)}
                      maxVisionCalls={getMaxVisionOverride(batch.agentId)}
                      onMaxVisionCallsChange={value => setMaxVisionForAgent(batch.agentId, value)}
                      visionModel={visionModel}
                      compact
                    />
                    {/* Per-document overrides are fixed at batch-creation time (Pending
                        Changes tab) and are not re-editable here — just surfaced so an
                        admin starting an older batch isn't surprised by them (AA-220
                        follow-up). */}
                    {batch.imageModeOverrides && Object.keys(batch.imageModeOverrides).length > 0 && (
                      <div style={{
                        marginTop: 8, fontSize: 11, color: '#92400e', background: '#fffbeb',
                        border: '1px solid #fde68a', borderRadius: 6, padding: '5px 8px',
                      }}>
                        <strong>{Object.keys(batch.imageModeOverrides).length}</strong> file{Object.keys(batch.imageModeOverrides).length !== 1 ? 's' : ''} will use
                        a different image strategy than the one above (set when this batch was created):{' '}
                        {Object.entries(batch.imageModeOverrides).map(([name, mode]) => `${name} → ${mode}`).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div style={{
                  padding: '8px 16px', display: 'flex', gap: 6, justifyContent: 'flex-end',
                  borderTop: `1px solid ${statusStyle.border}`,
                }}>
                  {batch.status === 'pending' && (
                    <button
                      onClick={() => handleApproveAndStartBatch(batch)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: 'none',
                        cursor: 'pointer', background: '#059669', color: '#fff',
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <Play size={14} /> Approve &amp; Start Indexing
                    </button>
                  )}
                  {batch.status === 'approved' && (
                    <button
                      onClick={() => handleTriggerIndex(batch)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: 'none',
                        cursor: 'pointer', background: '#059669', color: '#fff',
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <Play size={14} /> Start Indexing
                    </button>
                  )}
                  {batch.status === 'testing' && (
                    <>
                      <button
                        onClick={() => setTestingBatch(batch)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid #34d399',
                          cursor: 'pointer', background: 'var(--idx-bg-success)', color: '#047857',
                          fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <MessageSquare size={14} /> Test Index
                      </button>
                      <button
                        onClick={() => handleReject(batch)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: '1px solid #fecaca',
                          cursor: 'pointer', background: 'var(--idx-bg-primary)', color: '#dc2626',
                          fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Trash2 size={14} /> Reject & Delete
                      </button>
                      <button
                        onClick={() => handlePromote(batch)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, border: 'none',
                          cursor: 'pointer', background: '#16a34a', color: '#fff',
                          fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <ArrowUpCircle size={14} /> Promote to Active
                      </button>
                    </>
                  )}
                  {batch.status === 'indexing' && (
                    <button
                      onClick={() => handleCancelBatch(batch)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #fecaca',
                        cursor: 'pointer', background: 'var(--idx-bg-primary)', color: '#dc2626',
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <XCircle size={14} /> Cancel Indexing
                    </button>
                  )}
                  {batch.status === 'failed' && (
                    <button
                      onClick={() => handleCancelBatch(batch)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: 'none',
                        cursor: 'pointer', background: '#dc2626', color: '#fff',
                        fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <XCircle size={14} /> Dismiss
                    </button>
                  )}
                  {(batch.status === 'pending' || batch.status === 'approved') && (
                    <button
                      onClick={() => handleCancelBatch(batch)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid var(--idx-border-strong)',
                        cursor: 'pointer', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-muted)',
                        fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <XCircle size={14} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Test Panel slide-out */}
      {testingBatch && (
        <IndexTestPanel
          batch={testingBatch}
          onClose={() => setTestingBatch(null)}
          onPromote={(b) => { setTestingBatch(null); handlePromote(b); }}
          onReject={(b) => { setTestingBatch(null); handleReject(b); }}
        />
      )}
    </div>
  );
};

export default ChangeDetectionPanel;
